import clsx from "clsx";
import React from "react";
import { EditorialProductCard } from "web/react/components/editorials/editorial-product-card";
import { Heading } from "web/react/emo/heading";
import { ImageContainer } from "web/react/emo/image-container";
import { RichText } from "web/react/emo/rich-text";
import { useEditorialAnalytics } from "web/react/hooks/use-editorial-analytics";
import history from "web/script/utils/history";
import { EditorialShoppableSerializer } from "web/types/serializers";
import * as styles from "./editorial-shoppable.css";

export interface EditorialShoppableProps extends EditorialShoppableSerializer {
    className?: string;
    editorialLabel: string;
    gender: "womenswear" | "menswear" | "mixed";
    franchise: string;
    tags: string[];
    position: number;
    isSponsored: boolean;
}

export function EditorialShoppable({
    title,
    body,
    image_url: imageUrl,
    image_placement: imagePlacement = "left",
    image_credit: imageCredit,
    product_cards: productCards,
    editorialLabel,
    franchise,
    gender,
    tags,
    position,
    isSponsored,
    className,
}: EditorialShoppableProps): React.ReactElement {
    const { containerRef: visibilityRef } = useEditorialAnalytics({
        label: "shoppable_block",
        editorialLabel,
        franchise,
        tags,
        position,
        isSponsored,
        visibleThreshold: 0.5,
        gender,
    });

    function updateURLID(event: React.MouseEvent<HTMLDivElement>, id: string): void {
        const url = new URL(`${window.location.origin}${window.location.pathname}#${id}`);
        history.replaceState(url);
    }

    const titleId = title
        ? title.trim().replace(/\s+/g, "-").toLowerCase()
        : `shoppable-${position}`;

    return (
        <section className={className}>
            <div
                className={clsx(styles.container, {
                    [styles.reverseContainer]: imagePlacement === "right",
                })}
            >
                {imageUrl && (
                    <div className={styles.image} ref={visibilityRef}>
                        <ImageContainer imageSrc={imageUrl} imageCredit={imageCredit} />
                    </div>
                )}
                <div className={styles.content}>
                    {(title || body) && (
                        <div className={styles.textContent}>
                            <div
                                onClick={(event) => {
                                    updateURLID(event, titleId);
                                }}
                                id={titleId}
                                aria-hidden="true"
                            >
                                {title && (
                                    <Heading
                                        className={styles.title}
                                        textStyle="title-l"
                                        as="h2"
                                        upperCase
                                    >
                                        {title}
                                    </Heading>
                                )}
                                {body && (
                                    <RichText
                                        className={styles.body}
                                        textStyle={{ sm: "body-s", md: "body" }}
                                        color="secondary"
                                    >
                                        {body}
                                    </RichText>
                                )}
                            </div>
                        </div>
                    )}
                    <div className={styles.productGrid}>
                        {productCards.map((product, key) => (
                            <EditorialProductCard
                                key={product.id}
                                position={position}
                                productPosition={key + 1}
                                product={product}
                                franchise={franchise}
                                tags={tags}
                                gender={gender}
                                editorialLabel={editorialLabel}
                                isSponsored={isSponsored}
                                imageDataTestId={
                                    isSponsored
                                        ? "advertorial-product-card-image"
                                        : "editorial-product-image"
                                }
                                shortDescriptionDataTestId={
                                    isSponsored
                                        ? "advertorial-product-card-short-description"
                                        : "product-card-short-description"
                                }
                            />
                        ))}
                    </div>
                </div>
            </div>
        </section>
    );
}
